import React from 'react';
import { WxbIcon } from '../Icon/Icon';
import type { WxbIconName } from '../Icon/icons';
import { WxbTooltip } from '../Tooltip/Tooltip';
import './IconButton.css';

export interface WxbIconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: WxbIconName | React.ReactNode;
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  active?: boolean;
  tooltip?: React.ReactNode;
}

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const renderIcon = (icon: WxbIconName | React.ReactNode) => (
  typeof icon === 'string' ? <WxbIcon name={icon} size={16} /> : icon
);

export const WxbIconButton = React.forwardRef<HTMLButtonElement, WxbIconButtonProps>(({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  active = false,
  tooltip,
  className = '',
  type = 'button',
  ...props
}, ref) => {
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={active || undefined}
      className={joinClasses(
        'wxb-icon-button',
        `wxb-icon-button-${variant}`,
        `wxb-icon-button-${size}`,
        active && 'is-active',
        className,
      )}
      {...props}
    >
      {renderIcon(icon)}
    </button>
  );

  if (!tooltip) return button;

  return (
    <WxbTooltip title={tooltip}>
      {button}
    </WxbTooltip>
  );
});

WxbIconButton.displayName = 'WxbIconButton';
